import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, X, ChevronLeft, ChevronRight, Loader2, AlertCircle, ZoomIn, ZoomOut, RotateCcw, Maximize2, Minimize2 } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useUnifiedSignedUrl } from '@/hooks/useUnifiedSignedUrl';
import { getFileType, formatFileSize } from '@/lib/storageUtils';
import { logFilePreviewError } from '@/lib/securityLogger';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface FileInfo {
  name: string;
  bucket: string;
  path: string;
  size?: number;
}

interface UnifiedFilePreviewProps {
  file: FileInfo | null;
  files?: FileInfo[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
  portal?: 'charging' | 'crm' | 'inspector';
}

export function UnifiedFilePreview({
  file,
  files,
  open,
  onOpenChange,
  onNavigate,
  portal = 'crm',
}: UnifiedFilePreviewProps) {
  const { toast } = useToast();
  const [pdfPageNumber, setPdfPageNumber] = useState(1);
  const [pdfNumPages, setPdfNumPages] = useState<number | null>(null);
  const [imageError, setImageError] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [pdfScale, setPdfScale] = useState(1.0);
  const [pdfFitMode, setPdfFitMode] = useState<'width' | 'page' | 'custom'>('width');
  const [containerWidth, setContainerWidth] = useState(800);
  const [isPanning, setIsPanning] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [scrollPos, setScrollPos] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const { url, loading, error, refresh } = useUnifiedSignedUrl({
    bucket: file?.bucket || '',
    path: file?.path || null,
    enabled: open && !!file,
  });

  const fileType = file ? getFileType(file.name) : 'unknown';
  const canNavigate = files && files.length > 1 && onNavigate;

  const handleError = async (errorMessage: string, errorCode?: string) => {
    if (!file) return;

    const user = (await supabase.auth.getUser()).data.user;

    await logFilePreviewError({
      fileType,
      fileName: file.name,
      bucket: file.bucket,
      path: file.path,
      errorMessage,
      errorCode,
      userId: user?.id,
      portal,
    });
  };

  const handleImageError = () => {
    setImageError(true);
    handleError('Image failed to load');
  };

  const handlePdfError = (error: Error) => {
    setPdfError(true);
    handleError(`PDF failed to load: ${error.message}`);
  };

  const handleDownload = async () => {
    if (!url || !file) return;

    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.name;
      link.click();
      URL.revokeObjectURL(downloadUrl);

      toast({
        title: 'Download gestart',
        description: file.name,
      });
    } catch (err: any) {
      await handleError(`Download failed: ${err.message}`);
      
      toast({
        title: 'Download mislukt',
        description: 'Probeer het opnieuw',
        variant: 'destructive',
      });
    }
  };

  const handleRetry = () => {
    setImageError(false);
    setPdfError(false);
    refresh();
  };

  // PDF Zoom Controls
  const handleZoomIn = () => {
    setPdfScale(prev => Math.min(prev + 0.25, 3.0));
    setPdfFitMode('custom');
  };

  const handleZoomOut = () => {
    setPdfScale(prev => Math.max(prev - 0.25, 0.5));
    setPdfFitMode('custom');
  };

  const handleFitWidth = () => {
    setPdfFitMode('width');
    setPdfScale(1.0);
  };

  const handleFitPage = () => {
    setPdfFitMode('page');
    setPdfScale(0.85);
  };

  const handleResetZoom = () => {
    setPdfScale(1.0);
    setPdfFitMode('width');
  };

  // PDF Pan/Drag Controls
  const handleMouseDown = (e: React.MouseEvent) => {
    if (pdfScale <= 1.0) return;
    setIsPanning(true);
    setStartPos({ x: e.clientX, y: e.clientY });
    const container = containerRef.current;
    if (container) {
      setScrollPos({ x: container.scrollLeft, y: container.scrollTop });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isPanning || !containerRef.current) return;
    const dx = e.clientX - startPos.x;
    const dy = e.clientY - startPos.y;
    containerRef.current.scrollLeft = scrollPos.x - dx;
    containerRef.current.scrollTop = scrollPos.y - dy;
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  // Container resize observer
  useEffect(() => {
    if (!containerRef.current) return;
    
    const resizeObserver = new ResizeObserver(entries => {
      const width = entries[0].contentRect.width;
      setContainerWidth(width);
    });
    
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open || fileType !== 'pdf') return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === '+' || e.key === '=') {
          e.preventDefault();
          handleZoomIn();
        } else if (e.key === '-') {
          e.preventDefault();
          handleZoomOut();
        } else if (e.key === '0') {
          e.preventDefault();
          handleResetZoom();
        }
      }
      
      if (e.key === 'ArrowLeft' && pdfPageNumber > 1) {
        setPdfPageNumber(prev => prev - 1);
      } else if (e.key === 'ArrowRight' && pdfPageNumber < (pdfNumPages || 1)) {
        setPdfPageNumber(prev => prev + 1);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, fileType, pdfPageNumber, pdfNumPages, pdfScale]);

  if (!file) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[90vh] p-0 gap-0">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold truncate">{file.name}</h3>
            {file.size && (
              <p className="text-sm text-muted-foreground">{formatFileSize(file.size)}</p>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {canNavigate && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onNavigate('prev')}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onNavigate('next')}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </>
            )}
            
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDownload}
              disabled={!url}
            >
              <Download className="w-4 h-4" />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* PDF Controls Toolbar */}
        {fileType === 'pdf' && !pdfError && (
          <div className="border-b p-2 bg-muted/5 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomOut}
                disabled={pdfScale <= 0.5}
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              
              <span className="text-sm font-medium min-w-[60px] text-center">
                {Math.round(pdfScale * 100)}%
              </span>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomIn}
                disabled={pdfScale >= 3.0}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetZoom}
                title="Reset zoom"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant={pdfFitMode === 'width' ? 'default' : 'outline'}
                size="sm"
                onClick={handleFitWidth}
              >
                <Maximize2 className="h-4 w-4 mr-1" />
                Breedte
              </Button>
              
              <Button
                variant={pdfFitMode === 'page' ? 'default' : 'outline'}
                size="sm"
                onClick={handleFitPage}
              >
                <Minimize2 className="h-4 w-4 mr-1" />
                Pagina
              </Button>
            </div>
            
            {pdfNumPages && pdfNumPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPdfPageNumber(prev => Math.max(1, prev - 1))}
                  disabled={pdfPageNumber <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                <span className="text-sm">
                  {pdfPageNumber} / {pdfNumPages}
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPdfPageNumber(prev => Math.min(pdfNumPages, prev + 1))}
                  disabled={pdfPageNumber >= pdfNumPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div 
          ref={containerRef}
          className={`flex-1 overflow-auto bg-muted/30 flex items-center justify-center p-4 ${isPanning ? 'cursor-grabbing' : pdfScale > 1 ? 'cursor-grab' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {loading && (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Bestand laden...</p>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center gap-4 text-center max-w-md">
              <AlertCircle className="w-12 h-12 text-destructive" />
              <div>
                <h4 className="font-semibold mb-2">Kan bestand niet laden</h4>
                <p className="text-sm text-muted-foreground mb-4">{error}</p>
                <Button onClick={handleRetry} variant="outline">
                  Opnieuw proberen
                </Button>
              </div>
            </div>
          )}

          {!loading && !error && url && fileType === 'image' && !imageError && (
            <img
              src={url}
              alt={file.name}
              className="max-w-full max-h-full object-contain"
              onError={handleImageError}
            />
          )}

          {!loading && !error && url && fileType === 'pdf' && !pdfError && (
            <div className="flex flex-col items-center gap-4">
              <Document
                file={url}
                onLoadSuccess={({ numPages }) => setPdfNumPages(numPages)}
                onLoadError={handlePdfError}
                loading={
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <span>PDF laden...</span>
                  </div>
                }
              >
              <Page
                  pageNumber={pdfPageNumber}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="shadow-lg"
                  scale={pdfFitMode === 'custom' ? pdfScale : undefined}
                  width={pdfFitMode === 'width' ? containerWidth - 48 : pdfFitMode === 'page' ? Math.min(containerWidth - 48, 600) : undefined}
                />
              </Document>

              {pdfNumPages && (
                <div className="flex items-center gap-4 bg-background p-2 rounded-lg shadow">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPdfPageNumber(Math.max(1, pdfPageNumber - 1))}
                    disabled={pdfPageNumber <= 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  
                  <span className="text-sm">
                    Pagina {pdfPageNumber} van {pdfNumPages}
                  </span>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPdfPageNumber(Math.min(pdfNumPages, pdfPageNumber + 1))}
                    disabled={pdfPageNumber >= pdfNumPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {(imageError || pdfError) && (
            <div className="flex flex-col items-center gap-4 text-center max-w-md">
              <AlertCircle className="w-12 h-12 text-destructive" />
              <div>
                <h4 className="font-semibold mb-2">Kan bestand niet weergeven</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Er is een fout opgetreden bij het laden van dit bestand.
                </p>
                <Button onClick={handleRetry} variant="outline">
                  Opnieuw proberen
                </Button>
              </div>
            </div>
          )}

          {!loading && !error && url && fileType === 'unknown' && (
            <div className="flex flex-col items-center gap-4 text-center max-w-md">
              <AlertCircle className="w-12 h-12 text-muted-foreground" />
              <div>
                <h4 className="font-semibold mb-2">Geen preview beschikbaar</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Dit bestandstype kan niet worden weergegeven.
                </p>
                <Button onClick={handleDownload}>
                  <Download className="w-4 h-4 mr-2" />
                  Download bestand
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
